import { useEffect, useState } from 'react';
import { fetchCompanyCodeSets, type CodeDomain, type CompanyCodeSetDto } from '../api/company-codes';
import { registerSessionCache } from './session-caches';

/**
 * 기준 설정을 **쓰는 쪽** — 신청 화면이 이름을 여기서 읽는다 (2026-08-28 · 자리 재정의 판 7).
 *
 * ★★ **이것이 없으면 기준 설정은 거짓말이다.** 화면에서 「경조」를 「경조휴가」로 고쳐도
 *    신청 화면이 제 상수를 그리면 아무 데도 안 바뀐다 — 고칠 수 있는데 아무 일도 안 일어난다.
 *
 * ★ **바닥은 부르는 쪽이 갖는다.** 이 훅은 «회사가 손댄 것» 만 얹는다 — 못 읽었거나(404·오류)
 *   아직 못 받았으면 부르는 쪽의 기본 목록이 그대로 그려진다. **그래서 깜빡이지 않는다.**
 *
 * ★ 한 번 읽고 나눠 쓴다(모듈 캐시) — 모달을 열 때마다 왕복하지 않는다. 기준 설정 화면에서
 *   저장하면 `resetCompanyCodes()` 로 버려, 다음에 여는 화면이 새 이름을 집는다.
 */

const cache = new Map<CodeDomain, Promise<CompanyCodeSetDto[]>>();

/** 저장 뒤 캐시를 버린다 — 안 버리면 새 이름이 이 탭에서 안 보인다. */
export function resetCompanyCodes(): void {
  cache.clear();
}

function load(domain: CodeDomain): Promise<CompanyCodeSetDto[]> {
  const hit = cache.get(domain);
  if (hit) return hit;
  // ★ 옛 서버는 이 라우트를 모른다(404) — 빈 배열이면 부르는 쪽의 기본 목록이 그대로 산다.
  const p = fetchCompanyCodeSets(domain).catch(() => [] as CompanyCodeSetDto[]);
  cache.set(domain, p);
  return p;
}

export interface CompanyCodeView {
  /** 회사가 고친 이름 — 없으면 부르는 쪽이 준 이름 그대로. */
  labelOf: (setKey: string, code: string, fallback: string) => string;
  /** 회사가 「안 씀」으로 둔 값인가 — **읽기 전에는 언제나 false**(멀쩡한 선택지를 감추지 않는다). */
  isOff: (setKey: string, code: string) => boolean;
  /** 부르는 쪽의 기본 목록에 회사의 이름·씀 여부를 얹는다. */
  apply: <T extends { key: string; label: string }>(setKey: string, items: readonly T[]) => T[];
  ready: boolean;
}

export function useCompanyCodes(domain: CodeDomain): CompanyCodeView {
  const [sets, setSets] = useState<CompanyCodeSetDto[] | null>(null);

  useEffect(() => {
    let alive = true;
    void load(domain).then((s) => { if (alive) setSets(s); });
    return () => { alive = false; };
  }, [domain]);

  const find = (setKey: string, code: string) =>
    sets?.find((s) => s.key === setKey)?.codes.find((c) => c.code === code);

  const labelOf = (setKey: string, code: string, fallback: string) =>
    find(setKey, code)?.label || fallback;

  const isOff = (setKey: string, code: string) => find(setKey, code)?.active === false;

  const apply = <T extends { key: string; label: string }>(setKey: string, items: readonly T[]): T[] =>
    items
      .filter((i) => !isOff(setKey, i.key))
      .map((i) => ({ ...i, label: labelOf(setKey, i.key, i.label) }));

  return { labelOf, isOff, apply, ready: sets != null };
}

// ★ 로그아웃하면 비운다 (2026-08-31) — **회사가 다르면 값이 다르다**(테넌트 경계).
registerSessionCache(resetCompanyCodes);
